import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Flex, cn, toast } from '@kognitos/lattice';

import { ImportChecklistCard } from '@/modules/automation/components/ImportChecklistCard';
import { ErrorRenderer } from '@/shared/auth/components/ErrorRenderer';
import {
  ChatEdgeFade,
  CHAT_EDGE_FADE_CLEARANCE,
  CHAT_HEADER_FADE_CLEARANCE_CLASS,
  type IChatThreadHeaderLabels,
} from '@/shared/components';
import { parseStepReference } from '@/shared/utils/step-reference';
import { getRoutePath } from '@/routes/util';
import {
  getExtractAutomationCodeConnectionsQueryKey,
  getQueryAutomationQueryKey,
} from '@/generated/hooks/grimoire/v1/api';
import { getAutomationGetEventsQueryKey } from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import type { Rainv1Value } from '@/generated/hooks/rain/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import {
  RainChatThreadHeader,
  RainTodoCard,
  type TRainTodoVariant,
} from '@/modules/rain/components';

import { RainChatView } from '@/modules/rain/components/RainChatView';
import {
  ERainArtifactTool,
  RAIN_QUILL_AGENT_ID,
} from '@/modules/rain/agents/quill';
import { useQuillTestRun } from '@/modules/rain/agents/use-quill-test-run';
import {
  useAutomationRainAdapter,
  useAutomationThreadListAdapter,
  useRainChat,
} from '@/modules/rain/hooks';
import {
  selectClearPendingDraftMessage,
  selectPendingDraftMessage,
  selectTakePendingDraftMessage,
  useCreatingDraftStore,
} from '@/modules/drafts/stores/creating-draft.store';
import { buildRainUserMessage } from '@/modules/rain/utils/message';
import { captureException } from '@/modules/sentry';

interface IAutomationEditorChatProps {
  automationId: string;
  /**
   * Undefined while the thread is being created. Sending from the drafts hero
   * lands here immediately, before the thread exists, so this surface renders
   * its frame — header, selector, transcript, composer — and fills the thread
   * in when it arrives. The composer stays disabled until then.
   */
  rainThreadId: string | undefined;
  className?: string;
  /**
   * Fires when the user picks or creates a thread inside the selector. The
   * parent (AutomationEditor) owns the thread id, so this is just a setter
   * pass-through.
   */
  onSelectThread: (nextThreadId: string) => void;
  todoVariant?: TRainTodoVariant;
  /**
   * Drafts only. Enables the post-import validation checklist at the top of the
   * chat column. The checklist still self-hides unless the draft was created by
   * importing/moving an automation.
   */
  showImportChecklist?: boolean;
  /**
   * Drops the chat header's back button. Set it when the page header already
   * has one (the asset workspace), so the page doesn't show two.
   */
  hideBackButton?: boolean;
  /**
   * Fires (once per turn) when quill staged an artifact edit/write this turn
   * without committing it. The editor uses this to surface quill's staged
   * `preview.md` change as a reviewable diff. `save_automation` (a real commit)
   * is handled separately via the automation refetch.
   */
  onArtifactStaged?: () => void;
  /**
   * True when this chat is for a template-seeded preview draft that hasn't been
   * built yet. When the thread is empty, quill opens the conversation with the
   * review questions instead of leaving an empty chat (see the kickoff below).
   */
  isPreview?: boolean;
  /**
   * True while a requested build hasn't produced code yet. Holds the composer
   * closed for the whole build rather than just the turn in flight — the build
   * spans several quill turns, and the gaps between them used to let a message
   * land mid-build.
   */
  isBuildingBlueprint?: boolean;
  /** Tool-call id of the `test_automation`/`replay_run` run currently focused on the step view, if any. */
  focusedTestRunToolCallId?: string;
  /**
   * Run id of the currently focused run, if any — lets an organic
   * `replay_run` call auto-focus its new run when it's a direct continuation
   * of this one (see `useRainThreadMessages`'s `tool_result` handling).
   */
  focusedTestRunId?: string;
  /** Focus the step view on a `test_automation`/`replay_run` run (the chat card's "view run" click). */
  onFocusTestRun?: (
    run:
      | { toolCallId: string; runId: string; startedAtMs?: number }
      | undefined,
  ) => void;
  /**
   * Hands the chat's `sendTestRun` capability up to the parent once the
   * thread's hooks are mounted — the Test button lives in the page header,
   * outside this component, so it needs an imperative handle rather than a
   * prop drilled the other way.
   */
  onReady?: (api: {
    sendTestRun: (inputs: Record<string, Commonv1Value>) => Promise<void>;
  }) => void;
  /**
   * Runs before each send, e.g. on the live version, where a message is a
   * change that has to land in a draft. Resolving false holds the message
   * back in the composer.
   */
  onBeforeSend?: () => Promise<boolean>;
}

interface IAutomationEditorChatBodyProps {
  automationId: string;
  rainThreadId: string | undefined;
  todoVariant?: TRainTodoVariant;
  onArtifactStaged?: () => void;
  isPreview?: boolean;
  /**
   * When false, skip the empty-preview review kickoff. Used after the user
   * explicitly picks or creates another thread so New chat stays empty and
   * isolated (QA-661) instead of auto-resuming the prior review.
   */
  allowPreviewKickoff?: boolean;
  /**
   * True while a requested build hasn't produced code yet. Holds the composer
   * closed for the whole build rather than just the turn in flight — the build
   * spans several quill turns, and the gaps between them used to let a message
   * land mid-build.
   */
  isBuildingBlueprint?: boolean;
  focusedTestRunToolCallId?: string;
  focusedTestRunId?: string;
  onFocusTestRun?: (
    run:
      | { toolCallId: string; runId: string; startedAtMs?: number }
      | undefined,
  ) => void;
  /**
   * Reports whether the live plan has moved into the transcript, for the
   * floating Todo card — it renders outside this body and can't read `chat`.
   */
  onLivePlanRetiredChange?: (isRetired: boolean) => void;
  onReady?: (api: {
    sendTestRun: (inputs: Record<string, Commonv1Value>) => Promise<void>;
  }) => void;
  onBeforeSend?: () => Promise<boolean>;
}

/**
 * How long quill has to stay idle mid-build before we read it as waiting on the
 * user. Turn gaps are sub-second; this sits well clear of them.
 */
const BUILD_QUESTION_IDLE_MS = 4000;

/**
 * True once `condition` has held continuously for `delayMs`. Goes false the
 * instant the condition breaks.
 */
function useIdleFor(condition: boolean, delayMs: number): boolean {
  const [held, setHeld] = useState(false);
  // eslint-disable-next-line no-restricted-syntax -- reason: "has this held for N ms" is inherently a timer; no store or render can express it
  useEffect(() => {
    if (!condition) {
      setHeld(false);
      return;
    }
    const timer = setTimeout(() => setHeld(true), delayMs);
    return () => clearTimeout(timer);
  }, [condition, delayMs]);
  return held;
}

// Sent once, on the agent's behalf, into an EMPTY thread of a preview draft so
// quill opens with the gap-closing review questions rather than the user facing
// an empty chat. It's a review cue, NOT a build trigger (see the quill2
// preview_workflow); the `<user_action>` renders as a friendly labeled chip.
const REVIEW_KICKOFF_MESSAGE =
  '<user_action type="review_preview">I just opened this template preview. ' +
  'Review it and ask me the questions you need to make it concrete — which ' +
  'systems/connections, and any placeholder values or business rules. Do not ' +
  'build yet.</user_action>';

/**
 * Single-thread chat surface. AutomationEditorChat keys this on `rainThreadId` so a
 * thread switch tears the body down and rebuilds it fresh — messages,
 * streaming refs, and attachments all reset naturally.
 */
function AutomationEditorChatBody({
  automationId,
  rainThreadId,
  todoVariant = 'inline',
  onArtifactStaged,
  isPreview = false,
  allowPreviewKickoff = true,
  isBuildingBlueprint = false,
  focusedTestRunToolCallId,
  focusedTestRunId,
  onFocusTestRun,
  onLivePlanRetiredChange,
  onReady,
  onBeforeSend,
}: IAutomationEditorChatBodyProps) {
  const { t } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');

  const queryClient = useQueryClient();
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Automation-scoped (agent `quill2`) API surface for the editor chat. The
  // run-view focus rides along so the "view run" card can show and change it.
  const pendingDraftMessage = useCreatingDraftStore(selectPendingDraftMessage);
  const takePendingDraftMessage = useCreatingDraftStore(
    selectTakePendingDraftMessage,
  );
  const clearPendingDraftMessage = useCreatingDraftStore(
    selectClearPendingDraftMessage,
  );

  // Memoised on the CONTENT, not on the store object. Claiming the message for
  // sending replaces that object (`isDispatched` flips), and depending on it
  // handed the chat a new-but-identical draft mid-flight, which re-ran its
  // seeding effect and drew the user's message a second time.
  const isForThisAutomation =
    pendingDraftMessage?.automationId === automationId;
  const pendingContent = isForThisAutomation
    ? pendingDraftMessage.content
    : undefined;
  const pendingAttachments = isForThisAutomation
    ? pendingDraftMessage.attachments
    : undefined;

  const pendingFirstMessage = useMemo(
    () =>
      pendingContent === undefined
        ? null
        : { content: pendingContent, attachments: pendingAttachments ?? [] },
    [pendingContent, pendingAttachments],
  );

  const adapter = useAutomationRainAdapter(automationId, rainThreadId, {
    focusedToolCallId: focusedTestRunToolCallId,
    onFocus: onFocusTestRun,
  });

  // Run the editor's cache/side-effect policy as each tool succeeds:
  // - `save_automation` is a real commit → refetch the committed automation so
  //   the editor reflects the new AOP/manifest without waiting for the turn end.
  // - `artifact_edit`/`artifact_write` are staged (uncommitted) → tell the
  //   editor so it can surface quill's staged `preview.md` change as a diff.
  const handleToolSucceeded = useCallback(
    (toolName: string) => {
      if (toolName === ERainArtifactTool.SaveAutomation) {
        queryClient.invalidateQueries({
          queryKey: getQueryAutomationQueryKey(
            organization,
            workspace,
            automationId,
          ),
        });
        // Keyed without the stage filter so it matches DRAFT and PUBLISHED.
        queryClient.invalidateQueries({
          queryKey: getExtractAutomationCodeConnectionsQueryKey(
            organization,
            workspace,
            automationId,
          ),
        });
      }
      if (
        toolName === ERainArtifactTool.ArtifactEdit ||
        toolName === ERainArtifactTool.ArtifactWrite
      ) {
        onArtifactStaged?.();
      }
    },
    [automationId, onArtifactStaged, organization, queryClient, workspace],
  );

  // `sendToolCall` is created inside the thread hook, and the test-run protocol
  // needs it — so the protocol is wired through a ref the hook fills in below.
  const sendToolCallRef = useRef<
    (
      toolName: string,
      args: Record<string, Rainv1Value>,
    ) => Promise<string | undefined>
  >(async () => undefined);
  const sendToolCallBridge = useCallback(
    (toolName: string, args: Record<string, Rainv1Value>) =>
      sendToolCallRef.current(toolName, args),
    [],
  );

  const testRun = useQuillTestRun({
    sendToolCall: sendToolCallBridge,
    focusedRunId: focusedTestRunId,
    onFocusTestRun,
  });

  const formatStepSnippetLabel = useCallback(
    (snippetText: string) => {
      const stepReference = parseStepReference(snippetText);
      return stepReference
        ? t('automation.manifestSopView.step.chatReference', {
            number: stepReference.number,
            title: stepReference.title,
          })
        : snippetText;
    },
    [t],
  );

  const { chat, attachments, preview } = useRainChat({
    adapter,
    threadId: rainThreadId,
    pendingFirstMessage,
    onToolSucceeded: handleToolSucceeded,
    onVisibleToolResult: testRun.handleToolResult,
  });
  sendToolCallRef.current = chat.sendToolCall;
  const { abandonSeededTurn } = chat;

  // The drafts hero's message, in two parts.
  //
  // DISPLAY: read without claiming and handed to the chat, which draws it
  // immediately — before any thread exists — so the transcript shows what the
  // user sent from the first frame with the working state under it.
  //
  // SEND: claimed and sent once a thread exists. Through the RAW adapter, not
  // `sendMessageToThread`, precisely because the bubble is already on screen —
  // the higher-level send would draw its own and the user would see their
  // message twice.
  //
  // The once-only guard is `takePendingDraftMessage`, which marks and returns
  // in a single store update. It cannot be a ref: this component is keyed on
  // `rainThreadId`, so it remounts at the exact moment the thread lands and the
  // send fires, and a ref would be freshly empty right when it had to remember.
  // Sending a first message twice is what ENG-4145 is about.
  // eslint-disable-next-line no-restricted-syntax -- reason: fires once when the thread arrives, guarded by an atomic store claim
  useEffect(() => {
    if (!rainThreadId) return;
    const claimed = takePendingDraftMessage(automationId);
    if (!claimed) return;

    void adapter
      .sendMessage({
        message: buildRainUserMessage(claimed.content, claimed.attachments),
      })
      .then(() => {
        clearPendingDraftMessage();
      })
      .catch((error: unknown) => {
        // The claim is not handed back. A retry belongs to the user, through
        // the composer — silently re-sending on the next remount is how a
        // message goes out twice.
        clearPendingDraftMessage();
        // Which means the composer has to be usable. The seed dispatched
        // `start-turn` to draw the working state, and no server turn exists to
        // emit the `generation_complete` that would clear it, so the retry the
        // toast asks for would have nowhere to happen.
        abandonSeededTurn();
        toast.error(t('automation.quillChat.error.sendMessage'));
        captureException(error as Error, {
          tags: {
            component: 'AutomationEditorChat',
            action: 'sendPendingDraftMessage',
          },
        });
      });
  }, [
    rainThreadId,
    automationId,
    adapter,
    takePendingDraftMessage,
    clearPendingDraftMessage,
    abandonSeededTurn,
    t,
  ]);

  // Focus moves without the thread streaming anything, so the already-built
  // cards for the previously- and newly-focused runs have to be rebuilt for
  // their pressed state and label to catch up.
  const previousFocusedToolCallIdRef = useRef<string | undefined>(undefined);
  const { rebuildToolCards } = chat;
  // eslint-disable-next-line no-restricted-syntax -- reason: rebuilds already-cached tool-call card widgets in reaction to a focus prop change, not a data fetch
  useEffect(() => {
    rebuildToolCards([
      focusedTestRunToolCallId,
      previousFocusedToolCallIdRef.current,
    ]);
    previousFocusedToolCallIdRef.current = focusedTestRunToolCallId;
  }, [focusedTestRunToolCallId, rebuildToolCards]);

  // eslint-disable-next-line no-restricted-syntax -- reason: hands an imperative handle (sendTestRun) up to a parent outside this component, not a data fetch
  useEffect(() => {
    onReady?.({ sendTestRun: testRun.sendTestRun });
  }, [testRun.sendTestRun, onReady]);

  // Have quill open the conversation on a preview draft whose thread is EMPTY —
  // so the user isn't left facing an empty chat. Fires once per thread after
  // history has loaded and confirmed there are no messages (covers a freshly
  // created thread AND a pre-existing empty one, unlike gating on creation).
  const { messages, isLoadingHistory, isSending, isGenerating } = chat;
  const { sendMessageToThread } = chat;

  const { isLivePlanRetired } = chat;

  // eslint-disable-next-line no-restricted-syntax -- reason: hands the flag up to the floating Todo card, which renders outside this component
  useEffect(() => {
    onLivePlanRetiredChange?.(isLivePlanRetired);
  }, [isLivePlanRetired, onLivePlanRetiredChange]);

  // The build request explicitly invites clarifying questions, so quill may
  // stop mid-build and wait for an answer. Holding the composer shut for the
  // whole build would strand the user there, so release it once quill has been
  // idle long enough to mean "waiting on you" rather than "between turns" —
  // turn gaps are momentary, a question waits indefinitely. Scoped to the build
  // so the clock starts when the build does: the request is sent by the editor's
  // own mutation, not through this chat, so `isGenerating` doesn't rise until
  // the stream reports the turn, and an unscoped timer would already have
  // elapsed while the chat sat idle beforehand.
  const isQuillWaiting = useIdleFor(
    isBuildingBlueprint && !isGenerating,
    BUILD_QUESTION_IDLE_MS,
  );
  const composerDisabled = isBuildingBlueprint && !isQuillWaiting;

  // The build phase is read off the event log, but streamed events land in the
  // message store rather than that query's cache — so the log would keep
  // reporting `building` after a stop or failure until something refetched it.
  // A settling turn is exactly when a build-ending event has just landed, so
  // refresh it there. Scoped to a build in progress: outside one, nothing reads
  // the log closely enough to be worth a request per turn.
  const wasGeneratingRef = useRef(isGenerating);
  // eslint-disable-next-line no-restricted-syntax -- reason: refetch on a turn-settled edge; the streamed event never enters this query's cache, so no store or render can express it
  useEffect(() => {
    const settled = wasGeneratingRef.current && !isGenerating;
    wasGeneratingRef.current = isGenerating;
    if (!settled || !isBuildingBlueprint) return;
    void queryClient.invalidateQueries({
      queryKey: getAutomationGetEventsQueryKey(
        organization,
        workspace,
        automationId,
        RAIN_QUILL_AGENT_ID,
        rainThreadId,
        { compacted: false },
      ),
    });
  }, [
    automationId,
    isBuildingBlueprint,
    isGenerating,
    organization,
    queryClient,
    rainThreadId,
    workspace,
  ]);

  const kickoffSentForThreadRef = useRef<string | null>(null);
  // eslint-disable-next-line no-restricted-syntax -- reason: fire-once agent kickoff (a network side effect) into an empty preview thread once history has loaded
  useEffect(() => {
    // New chat / explicit thread switches must stay empty — do not re-run the
    // preview review that made parallel Quill threads look inherited (QA-661).
    if (!isPreview || !allowPreviewKickoff || !rainThreadId) return;
    if (isLoadingHistory) return; // wait until we know whether it's truly empty
    if (messages.length > 0 || isSending || isGenerating) return;
    if (kickoffSentForThreadRef.current === rainThreadId) return;
    kickoffSentForThreadRef.current = rainThreadId;
    void sendMessageToThread({ content: REVIEW_KICKOFF_MESSAGE });
  }, [
    allowPreviewKickoff,
    isPreview,
    rainThreadId,
    isLoadingHistory,
    messages.length,
    isSending,
    isGenerating,
    sendMessageToThread,
  ]);

  const todoCard = rainThreadId ? (
    <RainTodoCard
      automationId={automationId}
      agentId={RAIN_QUILL_AGENT_ID}
      threadId={rainThreadId}
      variant={todoVariant}
      isSending={chat.isSending || chat.isGenerating}
      isRetired={isLivePlanRetired}
    />
  ) : null;

  const viewChat = useMemo(
    () =>
      onBeforeSend
        ? {
            ...chat,
            sendMessageToThread: async (
              ...args: Parameters<typeof chat.sendMessageToThread>
            ) => (await onBeforeSend()) && chat.sendMessageToThread(...args),
          }
        : chat,
    [chat, onBeforeSend],
  );

  if (chat.backlogError) {
    return (
      <ErrorRenderer
        containerClassName="h-full"
        error={chat.backlogError}
        title={t('automation.quillChat.error.loadHistory.title')}
        emptyStateProps={{
          description: t('automation.quillChat.error.loadHistory.description'),
          actionLabel: tCommon('common.action.reload'),
          actionIcon: 'RefreshCw',
          onAction: () => window.location.reload(),
        }}
      />
    );
  }

  return (
    <RainChatView
      chat={viewChat}
      /* No scrollbar on the transcript: the edge fades already say there is
       * more above and below, and a bar over a 420px column reads as page
       * furniture. Scoped to the chat, or it would also take the scrollbar off
       * the attachment preview that mounts beside it. Same idiom `QuillChat`
       * uses for its horizontal bar. */
      className="[&_[data-slot=chat]_[data-slot=scroll-area-scrollbar]]:hidden"
      // "Selected" snippets here are manifest steps, so they read as
      // "Step 3 · Fetch invoices" rather than the raw quoted text.
      formatSnippetLabel={formatStepSnippetLabel}
      // Clears both fade strips, so neither the first nor the last message
      // comes to rest under a gradient.
      messageListClassName={cn(
        CHAT_HEADER_FADE_CLEARANCE_CLASS,
        CHAT_EDGE_FADE_CLEARANCE.bottom,
      )}
      attachments={attachments}
      preview={preview}
      composerDisabled={composerDisabled}
      // Persist the unsent composer text (editor only) so leaving the editor and
      // returning keeps what the user typed. Scoped by thread id into the single
      // editor slot, so only the last-edited draft is retained.
      draftScopeId={rainThreadId}
      // Inline Todo card sits OUTSIDE Chat's scroll area, above the ChatInput,
      // inset 8px each side so it's narrower than the full-width input.
      belowChatSlot={
        <>
          {/* Sits between the scroll area and whatever follows it, so the
              transcript dissolves into the composer the same way it does into
              the header. */}
          <ChatEdgeFade edge="bottom" />
          {todoVariant === 'inline' && todoCard && (
            <div className="-mx-1 w-full shrink-0 px-3 has-[[data-variant=inline]]:-mb-2">
              {todoCard}
            </div>
          )}
        </>
      }
    />
  );
}

/** Binds quill2's automation-scoped thread list to the chat column header. */
function EditorChatHeader({
  automationId,
  selectedThreadId,
  onSelectThread,
  hideBackButton,
}: {
  automationId: string;
  /** Undefined while the thread is being created — the selector shows its
   *  empty state, which is the same thing it shows for a thread with no name. */
  selectedThreadId: string | undefined;
  onSelectThread: (threadId: string) => void;
  hideBackButton?: boolean;
}) {
  const { t } = useTranslation('automation');
  const navigate = useNavigate();
  // The editor route carries both ids, so prefer them over the store: the
  // store defaults them to '' and an unhydrated store would send the back
  // button to `/organizations//workspaces//drafts`, which matches no route.
  const { orgId, workspaceId } = useParams<{
    orgId: string;
    workspaceId: string;
  }>();
  const storeOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const storeWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const currentOrganizationId = orgId || storeOrganizationId;
  const currentWorkspaceId = workspaceId || storeWorkspaceId;
  const adapter = useAutomationThreadListAdapter(
    automationId,
    RAIN_QUILL_AGENT_ID,
  );

  // The chat column only ever hosts a draft, so back always means the drafts
  // list — same route the page header's back arrow uses.
  const goToDrafts = () => {
    if (!currentOrganizationId || !currentWorkspaceId) return;
    navigate(
      getRoutePath('DRAFTS', {
        orgId: currentOrganizationId,
        workspaceId: currentWorkspaceId,
      }),
    );
  };

  const labels: IChatThreadHeaderLabels = {
    options: t('automation.chatHeader.options'),
    rename: t('automation.chatHeader.rename'),
    previousChats: t('automation.chatHeader.previousChats'),
    newChat: t('automation.chatHeader.newChat'),
    untitled: t('automation.chatHeader.untitled'),
    namePlaceholder: t('automation.chatHeader.namePlaceholder'),
  };

  return (
    <RainChatThreadHeader
      adapter={adapter}
      selectedThreadId={selectedThreadId}
      onSelectThread={onSelectThread}
      back={
        hideBackButton
          ? undefined
          : { label: t('automation.chatHeader.back'), onClick: goToDrafts }
      }
      labels={labels}
    />
  );
}

function AutomationEditorChat({
  automationId,
  rainThreadId,
  onSelectThread,
  className,
  todoVariant,
  showImportChecklist = false,
  hideBackButton = false,
  onArtifactStaged,
  isPreview = false,
  isBuildingBlueprint = false,
  focusedTestRunToolCallId,
  focusedTestRunId,
  onFocusTestRun,
  onReady,
  onBeforeSend,
}: IAutomationEditorChatProps) {
  const isFloating = todoVariant === 'floating';
  // Survives AutomationEditorChatBody remounts (keyed on rainThreadId). Once
  // the user picks or creates another thread, New chat must stay empty — the
  // preview review kickoff would otherwise re-ask the prior thread's questions
  // and look like inherited history (QA-661).
  const allowPreviewKickoffRef = useRef(true);
  // Only the body can see `chat`. Scoped to the thread it was reported for, or
  // the previous thread's flag applies until the remounted body reports again.
  const [livePlanRetired, setLivePlanRetired] = useState<{
    threadId: string | undefined;
    value: boolean;
  }>({ threadId: rainThreadId, value: false });
  const isLivePlanRetired =
    livePlanRetired.threadId === rainThreadId ? livePlanRetired.value : false;
  const handleLivePlanRetiredChange = useCallback(
    (value: boolean) => setLivePlanRetired({ threadId: rainThreadId, value }),
    [rainThreadId],
  );
  const handleSelectThread = useCallback(
    (nextThreadId: string) => {
      if (nextThreadId !== rainThreadId) {
        allowPreviewKickoffRef.current = false;
      }
      onSelectThread(nextThreadId);
    },
    [onSelectThread, rainThreadId],
  );
  return (
    <div className="relative h-full w-full">
      {isFloating && rainThreadId && (
        <RainTodoCard
          automationId={automationId}
          agentId={RAIN_QUILL_AGENT_ID}
          threadId={rainThreadId}
          variant="floating"
          isRetired={isLivePlanRetired}
        />
      )}
      <div className={cn('h-full w-full', isFloating && 'mx-auto max-w-3xl')}>
        <Flex
          direction="column"
          className={cn(
            'h-full pb-8',
            'transition-all duration-300 ease-in-out overflow-hidden',
            className,
          )}
        >
          {showImportChecklist && (
            <ImportChecklistCard automationId={automationId} />
          )}
          <EditorChatHeader
            automationId={automationId}
            selectedThreadId={rainThreadId}
            onSelectThread={handleSelectThread}
            hideBackButton={hideBackButton}
          />
          <AutomationEditorChatBody
            key={rainThreadId}
            automationId={automationId}
            rainThreadId={rainThreadId}
            todoVariant={todoVariant}
            onArtifactStaged={onArtifactStaged}
            isPreview={isPreview}
            allowPreviewKickoff={allowPreviewKickoffRef.current}
            isBuildingBlueprint={isBuildingBlueprint}
            focusedTestRunToolCallId={focusedTestRunToolCallId}
            focusedTestRunId={focusedTestRunId}
            onFocusTestRun={onFocusTestRun}
            onLivePlanRetiredChange={handleLivePlanRetiredChange}
            onReady={onReady}
            onBeforeSend={onBeforeSend}
          />
        </Flex>
      </div>
    </div>
  );
}

export default AutomationEditorChat;
